import React, { useMemo } from "react";
import { ModalWithIcon } from "@scripts/react/components/RfModal/ModalWithIcon";
import { UploadLimitErrorType } from "@/constants/media";

interface UploadLimitErrorModalProps {
    isOpen: boolean;
    onClose: () => void;
    errorType: UploadLimitErrorType | null;
}

export const UploadLimitErrorModal: React.FC<UploadLimitErrorModalProps> = ({
    isOpen,
    onClose,
    errorType
}) => {
    const errorContent = useMemo(() => {
        switch (errorType) {
            case UploadLimitErrorType.TOO_MANY_IMAGES:
                return {
                    title: "Too Many Images",
                    message: (
                        <>
                            <p className="mb-2 text-center">
                                You can upload a <strong>maximum of 10 images</strong> at a time for
                                testing.
                            </p>
                            <p className="text-center">
                                Please select up to 10 images and try again.
                            </p>
                        </>
                    )
                };
            case UploadLimitErrorType.TOO_MANY_VIDEOS:
                return {
                    title: "Too Many Videos",
                    message: (
                        <>
                            <p className="mb-2 text-center">
                                You can upload <strong>only 1 video</strong> at a time for testing.
                            </p>
                            <p className="text-center">
                                Please select a single video and try again.
                            </p>
                        </>
                    )
                };
            case UploadLimitErrorType.MIXED_CONTENT:
                return {
                    title: "Cannot Mix Images and Videos",
                    message: (
                        <>
                            <p className="mb-2 text-center">
                                You cannot upload images and videos together.
                            </p>
                            <p className="text-center">
                                Please upload either <strong>up to 10 images</strong> or{" "}
                                <strong>1 video</strong> at a time.
                            </p>
                        </>
                    )
                };
            default:
                return {
                    title: "Upload Error",
                    message: (
                        <p className="text-center">
                            An error occurred while uploading your files. Please try again.
                        </p>
                    )
                };
        }
    }, [errorType]);

    const { title, message } = errorContent;

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={onClose}
            type="warning"
            size="md"
            icon="far fa-exclamation-triangle"
            title={title}
            submitButtonText="Got it"
            showCancel={false}
        >
            {message}
        </ModalWithIcon>
    );
};
